// DailySchedule.js
import moment from 'moment';
import 'moment/locale/sv';
import React, { useEffect, useState } from 'react';
import fetchData from '../../ApiController/dataFetcher';
import { calculateAndFormatTimeRange } from '../calculateAndFormatTimeRange';
import CalendarComponent from './CalendarComponent';
import EventComponent from './EventComponent';
import { momentLocalizer } from 'react-big-calendar';
import './DailySchedule.css';

const DailySchedule = ({
  selectedPersonSign,
  setCourseId,
  setSelectedPersonSign,
  selectedSchedule,
  setSelectedSchedule,
}) => {
  const [schedule, setSchedule] = useState([]);
  const today = new Date();
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const { scheduleData } = await fetchData();
        setSchedule(scheduleData);
      } catch (error) {
        console.error('Error fetching schedule:', error.message);
      }
    };

    // Hämta data omedelbart om schedule är tom
    if (schedule.length === 0) {
      fetchDataAsync();
    }

    if (selectedPersonSign !== null || selectedSchedule !== null) {
      setSelectedEvent(null);
    } else {
      setSelectedEvent(selectedEvent);
    }

    // Updaterar leftContainer var 5e min
    const interval = setInterval(fetchDataAsync, 300000); // 300000 ms = 5 minuter

    // Uppröjningsfunktion för att rensa intervallet
    return () => clearInterval(interval);
  }, [selectedPersonSign, schedule.length, selectedEvent, selectedSchedule]); // Beroenden

  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

  const handleScheduleClick = (bokningsId) => {
    setCourseId((prevSign) => (prevSign === bokningsId ? null : bokningsId));
    setSelectedPersonSign(null);
    setSelectedSchedule(null);
    setSelectedEvent(selectedEvent === bokningsId ? null : bokningsId);
  };

  const getEventsForToday = () => {
    return schedule
      ?.map((event) => {
        const parsedDate = moment(event.datum, 'YYMMDD').format('YYYY-MM-DD');
        return {
          ...event,
          signatur: event.signatur,
          start: moment(`${parsedDate} ${event.startTid}`, 'YYYY-MM-DD HH:mm').toDate(),
          end: moment(`${parsedDate} ${event.slutTid}`, 'YYYY-MM-DD HH:mm').toDate(),
          title: <EventComponent event={event} />,
        };
      })
      .filter((event) => event.start >= startOfDay && event.end <= endOfDay);
  };

  const formats = {
    timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'HH:mm', culture),
    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      localizer.format(start, 'HH:mm', culture) + ' – ' + localizer.format(end, 'HH:mm', culture),
    dayHeaderFormat: (date, culture, localizer) => {
      const formattedDate = localizer.format(date, 'dddd, MMM D');
      const capitalizeFirstLetter = (str) => {
        return str.split(' ').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      };

      return capitalizeFirstLetter(formattedDate);
    },
  };

  const eventStyleGetter = (event) => {
    const isSignaturMatch = event.signatur.includes(selectedPersonSign);
    const isBokningsIdMatch = event.bokningsId.includes(selectedEvent);

    return {
      style: {
        ...(event.bokningsId === selectedSchedule && {
          backgroundColor: '#CD853F',
          color: 'white',
        }),
        ...(isSignaturMatch && { backgroundColor: '#CD853F', color: 'white' }),
        ...(isBokningsIdMatch && { border: '2px solid #CD853F' }),
      },
    };
  };

  const minStartTime = () => calculateAndFormatTimeRange('min', 'startTid', schedule);
  const maxEndTime = () => calculateAndFormatTimeRange('max', 'slutTid', schedule);

  const minTime = new Date(`2000-01-01T${minStartTime()}:00`);
  const maxTime = new Date(`2000-01-01T${maxEndTime()}:00`);

  return (
    <div className="daily-schedule">
      <CalendarComponent
        localizer={momentLocalizer(moment)}
        events={getEventsForToday()}
        formats={formats}
        minTime={minTime}
        maxTime={maxTime}
        eventStyleGetter={eventStyleGetter}
        onSelectEvent={(event) => handleScheduleClick(event.bokningsId)}
      />
    </div>
  );
};

export default DailySchedule;
